import { ChevronRight, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { getCatalogProducts, getFeaturedProducts, getShelves, shelfForLink, type Product, type Shelf } from './components/Product/productData';
import ProductCatalog from './components/ProductCatalog/ProductCatalog';
import { useShell } from './components/Shell/ShellContext';
import { ToolPage } from './components/ToolPage/ToolPage';
import { readRecent } from './components/ToolsSwitcher/recentTools';

const TOP_ROW_TILES = 4;

const TILE_CLASS =
  'flex min-w-0 flex-col items-center gap-1 rounded-xl px-0.5 py-1.5 text-center text-xs font-medium leading-[1.18] text-white no-underline transition-colors hover:bg-white/[0.07] hover:no-underline active:bg-white/10 sm:flex-row sm:gap-2.5 sm:px-2.5 sm:text-left sm:text-[0.9375rem]';

/** Music holds 12 of the tools, so it is the one tall shelf. Movies holds 2, so it spans the leftover width. */
const SHELF_LAYOUT: Record<string, { card: string; tiles: string; summaries?: boolean }> = {
  music: { card: 'sm:col-span-2 lg:col-span-1 lg:row-span-3', tiles: 'sm:grid-cols-4 lg:flex-1 lg:grid-cols-2 lg:auto-rows-fr' },
  movies: { card: 'sm:col-span-2', tiles: '', summaries: true },
};

function ToolTile({ product, showSummary }: { product: Product; showSummary?: boolean }) {
  return (
    <Link to={product.to} title={product.summary} className={`${TILE_CLASS} min-h-[68px] sm:min-h-14`}>
      <span aria-hidden className="select-none text-[1.4rem] leading-none sm:w-8 sm:shrink-0 sm:text-center sm:text-[1.6rem]">
        {product.icon}
      </span>
      <span className="min-w-0">
        <span className="block">{product.primary}</span>
        {showSummary ? (
          <span className="hidden text-[0.8125rem] font-normal leading-snug text-muted-foreground sm:block">{product.summary}</span>
        ) : null}
      </span>
    </Link>
  );
}

function ShelfCard({ shelf }: { shelf: Shelf }) {
  const layout = SHELF_LAYOUT[shelf.id];

  return (
    <section aria-label={shelf.name} className={`flex min-w-0 flex-col rounded-2xl bg-[#0f0f0f] p-1 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] sm:p-2 ${layout?.card ?? ''}`}>
      <h3 className="m-0">
        <Link
          to={`/?shelf=${shelf.id}`}
          aria-label={`${shelf.name}, ${shelf.products.length} tools`}
          className="label-mono flex min-h-10 items-center gap-1.5 rounded-lg px-2 no-underline hover:text-white hover:no-underline sm:px-2.5"
        >
          {shelf.name}
          <span className="tracking-normal text-white/60">{shelf.products.length}</span>
          <ChevronRight aria-hidden className="ml-auto size-3" />
        </Link>
      </h3>
      <div className={`grid grid-cols-[repeat(auto-fill,minmax(84px,1fr))] gap-0.5 sm:grid-cols-2 ${layout?.tiles ?? ''}`}>
        {shelf.products.map((product) => (
          <ToolTile key={product.to} product={product} showSummary={layout?.summaries} />
        ))}
      </div>
    </section>
  );
}

function PreviewCard({ product }: { product: Product }) {
  return (
    <Link
      to={product.to}
      className="flex w-[152px] shrink-0 snap-start flex-col overflow-hidden rounded-[14px] bg-[#111] text-white no-underline shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] transition-colors hover:bg-[#1a1a1a] hover:no-underline sm:w-auto"
    >
      <img src={`/previews${product.to}.webp`} width={400} height={225} alt="" className="aspect-video w-full bg-black object-cover" />
      <span className="flex flex-col gap-0.5 px-2.5 pb-2.5 pt-2">
        <span className="text-sm font-medium leading-tight">{product.primary}</span>
        <span className="text-[0.8125rem] leading-tight text-muted-foreground">{product.summary}</span>
      </span>
    </Link>
  );
}

function TopRow() {
  const catalog = getCatalogProducts();
  const recent = readRecent()
    .map((path) => catalog.find(({ to }) => to === path))
    .filter((product): product is Product => Boolean(product))
    .slice(0, TOP_ROW_TILES);

  if (recent.length === 0) {
    return (
      <section aria-label="Start Here">
        <h2 className="label-mono m-0 mb-1.5 pl-0.5">Start Here</h2>
        <div className="-mx-4 flex scroll-pl-4 snap-x snap-proximity gap-2.5 overflow-x-auto px-4 [scrollbar-width:none] sm:mx-0 sm:grid sm:grid-cols-3 sm:overflow-visible sm:px-0 lg:grid-cols-6">
          {getFeaturedProducts(catalog).map((product) => (
            <PreviewCard key={product.to} product={product} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Recent">
      <h2 className="label-mono m-0 mb-1.5 pl-0.5">Recent</h2>
      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        {recent.map((product) => (
          <Link
            key={product.to}
            to={product.to}
            className={`${TILE_CLASS} min-h-[68px] sm:min-h-[60px] sm:bg-[#111] sm:px-3 sm:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)] sm:hover:bg-[#1a1a1a]`}
          >
            <span aria-hidden className="select-none text-[1.4rem] leading-none sm:w-8 sm:shrink-0 sm:text-center sm:text-[1.6rem]">
              {product.icon}
            </span>
            <span className="min-w-0">
              <span className="block">{product.primary}</span>
              <span className="hidden truncate text-[0.8125rem] font-normal leading-snug text-muted-foreground lg:block">{product.summary}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function App() {
  const [params] = useSearchParams();
  const { openTools } = useShell();
  const shelf = shelfForLink(params);
  const toolCount = getCatalogProducts().length;

  if (shelf) {
    return (
      <ToolPage title={shelf.name} description={`${shelf.products.length} tools`} contentMaxWidth={720}>
        <ProductCatalog products={shelf.products} />
      </ToolPage>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-screen-xl flex-1 flex-col gap-3 px-4 pb-16 pt-3 sm:gap-6 sm:px-6 lg:pt-8">
      <header>
        <h1 className="font-display text-balance text-[clamp(1.875rem,5vw,2.75rem)] font-extrabold tracking-tight text-foreground">
          Hi there!
        </h1>
        <p className="mt-1 max-w-[60ch] text-pretty text-[0.9375rem] leading-snug text-muted-foreground sm:mt-1.5 sm:text-lg">
          I&apos;m Saarim, and I love building amazing things.{' '}
          <a href="https://saarim.me" target="_blank" rel="noopener noreferrer" className="whitespace-nowrap font-medium text-primary underline-offset-4 hover:underline">
            About me
          </a>
        </p>
      </header>

      <button
        type="button"
        onClick={openTools}
        className="flex h-[46px] w-full shrink-0 items-center gap-2 rounded-xl bg-[#141414] px-3 text-left text-base text-white/60 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)] md:hidden"
      >
        <Search aria-hidden className="size-5 shrink-0" />
        Search {toolCount} tools
      </button>

      <TopRow />

      <section aria-label={`All ${toolCount} tools`}>
        <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-4 pl-0.5">
          <h2 className="label-mono m-0">All {toolCount} Tools</h2>
          <p className="m-0 text-[0.8125rem] text-muted-foreground">Every tool opens with no account.</p>
        </div>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-3">
          {getShelves().map((item) => (
            <ShelfCard key={item.id} shelf={item} />
          ))}
        </div>
      </section>
    </div>
  );
}

export default App;
